// social-link-modal.component.ts
import {
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy,
  SimpleChanges,
  OnChanges,
  ChangeDetectorRef,
} from '@angular/core';
import {
  LearmoModalService,
  LearmoModalRef,
} from 'src/app/shared/services/learmo-modal.service';
import { ToastrService } from 'ngx-toastr';
import {
  SocialPlatform,
  websiteSocialMediaPlatforms,
  WebsiteSocialLink,
} from '../../../portal.model';
import { SocialLinksService } from '../shared-social-links.service';

interface PlatformOption {
  platform: SocialPlatform;
  icon: string;
  label: string;
}

interface FormErrors {
  platform: string;
  url: string;
}

@Component({
  selector: 'app-social-link-modal',
  templateUrl: './social-link-modal.component.html',
  styleUrls: ['./social-link-modal.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SocialLinkModalComponent implements OnChanges {
  @ViewChild('modalContent') modalContent!: TemplateRef<any>;

  @Input() mode: 'add' | 'edit' = 'add';
  @Input() editLink: WebsiteSocialLink | null = null;
  @Input() opener = false;

  @Output() openerChange = new EventEmitter<boolean>();
  @Output() saveLink = new EventEmitter<WebsiteSocialLink>();
  // @Output() cancel = new EventEmitter<void>();

  modalRef?: LearmoModalRef;

  platform = '';
  url = '';
  errors: FormErrors = { platform: '', url: '' };

  readonly platforms: PlatformOption[] = websiteSocialMediaPlatforms;

  constructor(
    private modalService: LearmoModalService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private linksService: SocialLinksService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['opener']?.currentValue && !changes['opener'].previousValue) {
      this.openModal();
    }

    if (changes['editLink']) {
      this.patchForm();
    }
  }

  get existingPlatforms(): string[] {
    return this.linksService.existingPlatforms;
  }

  get modalTitle(): string {
    return this.mode === 'add' ? 'Add Social Link' : 'Edit Social Link';
  }

  get saveButtonText(): string {
    return this.mode === 'add' ? 'Add Link' : 'Save Changes';
  }

  get availablePlatforms(): PlatformOption[] {
    if (this.mode === 'edit') {
      return this.platforms;
    }

    const existingSet = new Set(this.existingPlatforms);
    return this.platforms.filter((p) => !existingSet.has(p.platform));
  }

  get isPlatformDisabled(): boolean {
    return this.mode === 'edit';
  }

  openModal(): void {
    this.resetForm();
    this.patchForm();

    this.modalRef = this.modalService.open(this.modalContent, {
      title: this.modalTitle,
      icon: 'share-2',
      size: 'lg',
      premium: true,
      footer: this.modalFooter,
    });

    this.modalRef.dismissed.subscribe(() => this.handleModalClose());
  }

  @ViewChild('modalFooter') modalFooter!: TemplateRef<any>;

  closeModal(): void {
    this.modalRef?.close();
  }

  handleSave(): void {
    if (!this.validateForm()) {
      this.toastr.warning('Please fix the errors before saving');
      this.cdr.markForCheck();
      return;
    }

    const linkData = this.buildLinkData();
    this.saveLink.emit(linkData);
    this.closeModal();
  }

  private validateForm(): boolean {
    this.resetErrors();
    let isValid = true;

    if (!this.platform) {
      this.errors.platform = 'Please select a platform';
      isValid = false;
    }

    const trimmedUrl = this.url.trim();
    if (!trimmedUrl) {
      this.errors.url = 'URL is required';
      isValid = false;
    } else if (!this.isValidHttpsUrl(trimmedUrl)) {
      this.errors.url = 'URL must start with https://';
      isValid = false;
    }

    return isValid;
  }

  private isValidHttpsUrl(url: string): boolean {
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'https:';
    } catch {
      return false;
    }
  }

  private buildLinkData(): WebsiteSocialLink {
    return {
      id: this.editLink?.id ?? '',
      platform: this.platform as SocialPlatform,
      url: this.url.trim(),
      order: this.editLink?.order ?? 0,
    };
  }

  private patchForm(): void {
    if (this.editLink) {
      this.platform = this.editLink.platform;
      this.url = this.editLink.url;
    }
    this.resetErrors();
    this.cdr.markForCheck();
  }

  private resetForm(): void {
    this.platform = '';
    this.url = '';
    this.resetErrors();
  }

  private resetErrors(): void {
    this.errors = { platform: '', url: '' };
  }

  private handleModalClose(): void {
    this.openerChange.emit(false);
    // this.cancel.emit();
    this.resetForm();
  }
}
